import api from './api';

export interface LogEntry {
  id: string;
  timestamp: Date;
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  data?: any;
  source: string;
}

// COMPLETELY DISABLED FOR DEBUGGING
class LogService {
  private syncInterval: number | null = null;
  private initialized: boolean = false;

  constructor() {
    // DISABLED
  }

  private init() {
    if (this.initialized) return;
    this.initialized = true;
    this.startSync();
    this.setupErrorCapture();
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private addLog(level: LogEntry['level'], message: string, data?: any, source: string = 'general') {
    // Lazy init on first log
    this.init();
    
    const entry: LogEntry = {
      id: this.generateId(),
      timestamp: new Date(),
      level,
      message,
      data,
      source,
    };

    // Save to localStorage immediately (NO RAM storage)
    this.saveToLocalStorage(entry);
    
    // Console output only (no capture to avoid loops)
    const consoleMethod = level === 'error' ? console.error : 
                         level === 'warn' ? console.warn : 
                         level === 'debug' ? console.debug : console.log;
    consoleMethod(`[${source}] ${message}`, data || '');

    // Try immediate sync for errors
    if (level === 'error') {
      this.sendToBackend(entry);
    }
  }

  private setupErrorCapture() {
    // Capture unhandled errors - only send to backend/localStorage, NOT to console to avoid loops
    window.addEventListener('error', (event) => {
      const entry: LogEntry = {
        id: this.generateId(),
        timestamp: new Date(),
        level: 'error',
        message: 'Unhandled error: ' + event.message,
        data: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error?.toString(),
          stack: event.error?.stack,
        },
        source: 'GlobalErrorHandler',
      };
      this.saveToLocalStorage(entry);
      this.sendToBackend(entry);
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      const entry: LogEntry = {
        id: this.generateId(),
        timestamp: new Date(),
        level: 'error',
        message: 'Unhandled promise rejection',
        data: {
          reason: event.reason?.toString?.() || event.reason,
          stack: event.reason?.stack,
        },
        source: 'GlobalErrorHandler',
      };
      this.saveToLocalStorage(entry);
      this.sendToBackend(entry);
    });
  }

  private saveToLocalStorage(entry: LogEntry) {
    try {
      const key = `error_log_${entry.timestamp.toISOString().split('T')[0]}`;
      const existing = localStorage.getItem(key);
      const logs = existing ? JSON.parse(existing) : [];
      logs.push({
        ...entry,
        timestamp: entry.timestamp.toISOString(),
      });
      // Keep only last 50 errors per day to save space
      if (logs.length > 50) logs.shift();
      localStorage.setItem(key, JSON.stringify(logs));
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  private startSync() {
    // Sync every 60 seconds
    this.syncInterval = window.setInterval(() => {
      this.syncAllPending();
    }, 60000);

    // Also sync on page unload
    window.addEventListener('beforeunload', () => {
      this.syncAllPending();
    });
  }

  private async sendToBackend(entry: LogEntry) {
    try {
      await api.post('/logs/frontend', {
        level: entry.level,
        message: entry.message,
        data: entry.data ? JSON.stringify(entry.data) : null,
        source: entry.source,
        url: window.location.href,
        errorStack: entry.data?.stack || entry.data?.error?.stack,
      });
    } catch (error) {
      // Backend failed - already saved in localStorage, will retry later
    }
  }

  private syncAllPending() {
    // Get all unsynced logs from localStorage and send to backend
    const keys = Object.keys(localStorage).filter(k => k.startsWith('error_log_'));
    keys.forEach(key => {
      try {
        const logs = JSON.parse(localStorage.getItem(key) || '[]');
        logs.forEach((log: any) => {
          if (!log.synced) {
            this.sendToBackend({
              ...log,
              timestamp: new Date(log.timestamp),
            });
            log.synced = true;
          }
        });
        localStorage.setItem(key, JSON.stringify(logs));
      } catch (e) {
        // Ignore errors
      }
    });
  }

  // Public API - NO in-memory storage
  info(message: string, data?: any, source?: string) {
    this.addLog('info', message, data, source);
  }

  warn(message: string, data?: any, source?: string) {
    this.addLog('warn', message, data, source);
  }

  error(message: string, data?: any, source?: string) {
    this.addLog('error', message, data, source);
  }

  debug(message: string, data?: any, source?: string) {
    this.addLog('debug', message, data, source);
  }

  // Get logs from localStorage (NOT from memory)
  getLocalStorageErrors(date?: string): any[] {
    const key = date 
      ? `error_log_${date}` 
      : `error_log_${new Date().toISOString().split('T')[0]}`;
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }

  // Get all logs from all dates
  getAllLocalStorageErrors(): any[] {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('error_log_'));
    let allLogs: any[] = [];
    keys.forEach(key => {
      try {
        const logs = JSON.parse(localStorage.getItem(key) || '[]');
        allLogs = allLogs.concat(logs);
      } catch (e) {
        // Ignore
      }
    });
    return allLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }

  clearLocalStorageLogs() {
    const keys = Object.keys(localStorage).filter(k => k.startsWith('error_log_'));
    keys.forEach(k => localStorage.removeItem(k));
  }

  exportLogs(): string {
    return JSON.stringify(this.getAllLocalStorageErrors(), null, 2);
  }

  // Get logs from backend (for admin)
  async getBackendLogs(level?: string, limit: number = 100): Promise<any[]> {
    try {
      const response = await api.get('/logs/frontend', {
        params: { level, limit }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch logs from backend', error);
      return [];
    }
  }
}

// Lazy singleton - no initialization during module load
let logServiceInstance: LogService | null = null;

export const getLogService = () => {
  if (!logServiceInstance) {
    logServiceInstance = new LogService();
  }
  return logServiceInstance;
};

// Export singleton for backward compatibility (lazy init)
export const logService = new LogService();

// NO console override to prevent infinite loops
// Expose to window for debugging
(window as any).logService = logService;
